import React, { useEffect, useState } from 'react';
import {
  Drawer, DrawerProps, Button, Form, NumberInput, Radio, RadioGroup, Message, Loader, Tag, Stack, Input, Divider,
} from 'rsuite';
import { AxiosError } from 'axios';
import ErrorMessage from '@/components/ErrorMessage';
import ZonedDatePicker from '@/components/ZonedDatePicker';
import { BaseErrorModel } from '@/models/core.model';
import {
  EnergyHubClubRunCandidate, EnergyHubReceipt, EnergyHubReceiptMatchSource, EnergyHubReceiptMatchSourceList,
  EnergyHubReceiptStatus, energyHubTimeZone, formatHkd,
} from '@/models/energy-hub.model';
import {
  approveEnergyHubReceipt, getEnergyHubReceiptCandidates, rejectEnergyHubReceipt,
} from '@/services/energy-hub.service';
import { toastSuccessMessage } from '@/utils/toasterMessage';
import { jsDateToZone } from '@/utils/date-time';
import { EnergyHubReceiptStatusTag } from '../list/energy-hub-status-tag';

/** RadioGroup value for "no club run". */
const SOLO = 0;

const gapLabel = (minutes: number) => {
  if (minutes == 0) return 'inside the check-in window';
  if (minutes < 60) return `${minutes} min from the window`;
  const h = Math.floor(minutes / 60);
  return h < 48 ? `${h} h from the window` : `${Math.floor(h / 24)} days from the window`;
};

interface Props extends DrawerProps {
  receipt?: EnergyHubReceipt;
  onReviewed?: () => void;
}

const ReceiptReviewDrawer = (props: Props) => {
  const { open, onClose, onReviewed, receipt, ...rest } = props;
  const [receiptTime, setReceiptTime] = useState<Date | null>(null);
  const [amount, setAmount] = useState<number | undefined>(undefined);
  const [link, setLink] = useState<number>(SOLO);
  const [candidates, setCandidates] = useState<EnergyHubClubRunCandidate[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);

  const isPending = receipt?.status == EnergyHubReceiptStatus.Pending;

  useEffect(() => {
    if (!open || !receipt) return;
    setErrorModel(undefined);
    setReceiptTime(receipt.receiptTime ?? null);
    setAmount(receipt.amount ?? undefined);
    setLink(receipt.clubVisitId ?? SOLO);
    setRejectReason('');
    setRejecting(false);
    setCandidates([]);
  }, [open, receipt]);

  // Re-rank whenever staff type the time printed on the receipt.
  useEffect(() => {
    if (!open || !receipt || !isPending) return;
    let cancelled = false;
    setLoadingCandidates(true);
    getEnergyHubReceiptCandidates(receipt.id, receiptTime ?? undefined)
      .then(res => {
        if (cancelled) return;
        const list = res.result ?? [];
        setCandidates(list);
        // Keep the runner's own pick; otherwise pre-select the API's suggestion.
        if (!receipt.clubVisitId) {
          const suggested = list.find(c => c.isSuggested);
          setLink(suggested ? suggested.clubVisitId : SOLO);
        }
      })
      .catch(err => { if (!cancelled) setErrorModel((err as AxiosError<BaseErrorModel>).response?.data); })
      .finally(() => { if (!cancelled) setLoadingCandidates(false); });
    return () => { cancelled = true; };
  }, [open, receipt, receiptTime]);

  const handleClose = (event?: React.SyntheticEvent) => {
    setSaving(false);
    onClose?.(event as any);
  };

  const run = async (action: () => Promise<any>, message: string) => {
    setErrorModel(undefined);
    setSaving(true);
    try {
      const result = await action();
      if (result.success) {
        toastSuccessMessage(message);
        onReviewed?.();
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = () => {
    if (!receipt) return;
    run(() => approveEnergyHubReceipt(receipt.id, {
      clubVisitId: link == SOLO ? null : link,
      receiptTime: receiptTime ?? undefined,
      amount: amount == null || (amount as any) === '' ? undefined : Number(amount),
    }), 'Receipt approved');
  };

  const handleReject = () => {
    if (!receipt || !rejectReason.trim()) return;
    run(() => rejectEnergyHubReceipt(receipt.id, rejectReason.trim()), 'Receipt rejected');
  };

  // The runner's pick may be older than the candidate lookback: keep it selectable.
  const runnerPickMissing = !!receipt?.clubVisitId && !candidates.some(c => c.clubVisitId == receipt.clubVisitId);

  return (
    <Drawer backdrop="static" size="lg" placement="right" open={open} onClose={handleClose} {...rest}>
      <Drawer.Header>
        <Drawer.Title>Receipt #{receipt?.id}</Drawer.Title>
        <Drawer.Actions>
          {isPending && !rejecting && (
            <>
              <Button onClick={handleApprove} loading={saving} appearance="primary" color="green">Approve</Button>
              <Button onClick={() => setRejecting(true)} appearance="ghost" color="red">Reject…</Button>
            </>
          )}
          <Button onClick={handleClose} appearance="subtle">Close</Button>
        </Drawer.Actions>
      </Drawer.Header>
      <Drawer.Body>
        {receipt && (
          <Stack alignItems="flex-start" spacing={24} wrap>
            <div style={{ flex: '1 1 320px', maxWidth: 420 }}>
              <a href={receipt.imageUrl} target="_blank" rel="noreferrer">
                <img src={receipt.imageUrl} alt="Receipt" style={{ width: '100%', borderRadius: 8, border: '1px solid #ddd' }} />
              </a>
              <div style={{ color: '#888', fontSize: 12, marginTop: 6 }}>Click to open full size.</div>
            </div>

            <div style={{ flex: '1 1 360px' }}>
              <Message style={{ marginBottom: 16 }}>
                <div><b>{receipt.hubName}</b></div>
                <div>Runner: {receipt.userName ?? `#${receipt.userId}`}</div>
                <div>Submitted: {jsDateToZone(receipt.submittedAt, energyHubTimeZone)} HK</div>
                <div style={{ marginTop: 6 }}><EnergyHubReceiptStatusTag status={receipt.status} /></div>
              </Message>

              {!isPending && (
                <Message type={receipt.status == EnergyHubReceiptStatus.Approved ? 'success' : 'error'} showIcon>
                  {receipt.status == EnergyHubReceiptStatus.Approved ? (
                    <>
                      Approved {jsDateToZone(receipt.reviewedAt, energyHubTimeZone)} HK as{' '}
                      <b>{receipt.clubEventName ? `club run "${receipt.clubEventName}"` : 'a solo purchase'}</b>
                      {receipt.clubEventName && ` (${EnergyHubReceiptMatchSourceList.find(m => m.value == receipt.matchSource)?.label})`}.
                      <br />Receipt time: {jsDateToZone(receipt.receiptTime, energyHubTimeZone)} HK · Amount: {formatHkd(receipt.amount)}
                    </>
                  ) : (
                    <>Rejected {jsDateToZone(receipt.reviewedAt, energyHubTimeZone)} HK: {receipt.rejectReason}</>
                  )}
                </Message>
              )}

              {isPending && rejecting && (
                <Form fluid>
                  <Form.Group>
                    <Form.Label>Reject reason (shown to the runner)</Form.Label>
                    <Input as="textarea" rows={3} value={rejectReason} maxLength={500} onChange={setRejectReason}
                      placeholder="e.g. The receipt is not from this venue / the photo is unreadable." />
                  </Form.Group>
                  <Stack spacing={8}>
                    <Button appearance="primary" color="red" onClick={handleReject} loading={saving} disabled={!rejectReason.trim()}>
                      Confirm reject
                    </Button>
                    <Button appearance="subtle" onClick={() => setRejecting(false)}>Back</Button>
                  </Stack>
                </Form>
              )}

              {isPending && !rejecting && (
                <Form fluid disabled={saving}>
                  <Form.Group>
                    <Form.Label>Time on the receipt (HK)</Form.Label>
                    <ZonedDatePicker block value={receiptTime} zoneIanaId={energyHubTimeZone} format="yyyy-MM-dd HH:mm"
                      onChange={setReceiptTime} />
                    <Form.HelpText>
                      Used to suggest the matching club run.{receipt.receiptTime && ' Prefilled with what the runner entered — check it against the photo.'}
                    </Form.HelpText>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Amount (HK$, optional)</Form.Label>
                    <NumberInput min={0} value={amount ?? null}
                      onChange={v => setAmount(v === null || v === '' ? undefined : Number(v))} />
                    {receipt.amount != null && <Form.HelpText>Prefilled with what the runner entered.</Form.HelpText>}
                  </Form.Group>

                  <Divider style={{ margin: '12px 0' }}>Club run</Divider>
                  {loadingCandidates ? <Loader content="Finding club runs…" /> : (
                    <RadioGroup value={link} onChange={v => setLink(Number(v))}>
                      {candidates.map(c => (
                        <Radio key={c.clubVisitId} value={c.clubVisitId}>
                          <b>{c.eventName}</b>{c.clubName && ` · ${c.clubName}`}
                          <div style={{ fontSize: 12, color: '#888' }}>
                            {jsDateToZone(c.startTime, energyHubTimeZone)} HK · {gapLabel(c.gapMinutes)}
                            {c.checkedInAtHub && <Tag size="sm" color="green" style={{ marginLeft: 6 }}>Checked in here</Tag>}
                            {c.isSuggested && <Tag size="sm" color="blue" style={{ marginLeft: 6 }}>Suggested</Tag>}
                            {receipt.clubVisitId == c.clubVisitId && receipt.matchSource == EnergyHubReceiptMatchSource.Runner &&
                              <Tag size="sm" color="violet" style={{ marginLeft: 6 }}>Runner picked</Tag>}
                          </div>
                        </Radio>
                      ))}
                      {runnerPickMissing && (
                        <Radio value={receipt.clubVisitId!}>
                          <b>{receipt.clubEventName ?? `Hub Run #${receipt.clubVisitId}`}</b>
                          <Tag size="sm" color="violet" style={{ marginLeft: 6 }}>Runner picked</Tag>
                        </Radio>
                      )}
                      <Radio value={SOLO}><b>Solo purchase</b> (not on a club run)</Radio>
                    </RadioGroup>
                  )}
                  {!loadingCandidates && candidates.length == 0 && !runnerPickMissing && (
                    <Message type="info" style={{ marginTop: 8 }}>
                      This runner was on no Hub Run at this venue in the last 30 days.
                    </Message>
                  )}
                  <Message type="info" style={{ marginTop: 16 }}>
                    Approving charges the venue the purchase fee, less any check-in fee already charged for the same run,
                    and gives the runner +1 coin.
                  </Message>
                </Form>
              )}
              {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 16 }} />}
            </div>
          </Stack>
        )}
      </Drawer.Body>
    </Drawer>
  );
};

export default ReceiptReviewDrawer;
